/**
 * Representation of the WorkOfArt Entity
 * 
 * @author Leander Eyer <leander.eyer@elead.ch>
 */

export { WorkOfArt };

import { ImageList } from './ImageList.js';
import { filterNonNumeric, hasNumeric } from './../../util.js';

/** Work Entity */
class WorkOfArt {

	constructor(model, data) {

		// Properties
		this.model = model;
		this.id = data.id;
		
		this.title = data.title.trim();
		this.description = data.description != null? data.description.trim() : "";
		
		this.location = data.location.trim();
		this.origin = data.origin.trim();
		this.copyright = typeof data.copyright !== 'undefined'? data.copyright.trim() : '';

		this.year = data.datingYear.trim();
		this.sortYear = this.year.length > 4? this.year.substring(0, 4) : this.year;

		this.technique = data.technique.trim();
		this.flagTimeline = data.flagTimeline;
		this.flagHideInArtistList = data.flagHideInArtistList;

		this.length = data.length.trim();
		this.height = data.height.trim();
		this.width = data.width.trim();
		
		this.image = new ImageList(data.image);

		// transient state
		this.isInSelection = false;

		// Relations
		this.tags = [];
		this.artists = [];
		this.collections = [];
		this.exhibitions = [];
	}
	
	// Basic Getters
	
	getID() {
		return this.id;
	}
	
	getTitle() {
		return this.title;
	}
	
	getDescription() {
		return this.description;
	}
	
	getLocation() {
		return this.location;
	}
	
	getOrigin() {
		return this.origin;
	}

	getCopyright() {
		return this.copyright;
	}

	getYear() {
		return this.year;
	}
	
	getTechnique() {
		return this.technique;
	}
	
	getFlagTimeline() {
		return this.flagTimeline;
	}
	
	getFlagHideInArtistList() {
		return this.flagHideInArtistList;
	}

	getImages() {
		return this.image;
	}
	
	// Relations
	
	getTags() {
		return this.tags;
	}
	
	addTag(tag) {
		this.tags.push(tag);
	}
	
	/** Test if this Work of Art is tagged wtih a specific tag */
	hasTag(tag) {
		for (let i = 0; i < this.tags.length; i++) {
			if (this.tags [i].getLabel() == tag) {
				return true;
			}
		}
		return false;
	}
	
	getArtists() {
		return this.artists;
	}
	
	addArtist(artist) {
		this.artists.push(artist);
	}
	
	getCollections() {
		return this.collections;
	}
	
	addCollection(collection) {
		this.collections.push(collection);
	}
	
	/** Test if this Work of Art is in a collection with the specified name */
	isInCollection(collection) {
		for (let i = 0; i < this.collections.length; i++) {
			if (this.collections [i].getName().toLowerCase() == collection) {
				return true;
			}
		}
		return false;		
	}
	
	getExhibitions() {
		return this.exhibitions;
	}
	
	addExhibition(exhibition) {
		this.exhibitions.push(exhibition);
	}
	
	// Utility
	
	/** Get the basic (unencoded) URL Term form for this entity */
	getURLTerm() {
		return this.getID();		
	}

	/** Equality Test Used for URL Resovling */
	acceptURLTerm(term) {
		return this.getURLTerm() == term;
	}

	/** Filter for a search Term */
	accept(filter) {
		if (Array.isArray(filter)) {
			// composed search terms must match all subcomponents
			for (let i = 0; i < filter.length; i++) {
				if (this.accept(filter [i]) == false) {
					return false;
				}
			}
		} else {
			// individual terms can match the work of art itself,
			// or any of the relations of the work (tag, exhibition, collection etc)
			if (filter.startsWith("tag=")) {
				let tag = filter.substring(filter.indexOf("=") + 1);
				if (this.hasTag(tag) == false) {
					return false;
				}
			} else if (filter.startsWith("collection=")) {
				let sammlung = filter.substring(filter.indexOf("=") + 1);
				if (this.isInCollection(sammlung) == false) {
					return false;
				}
			} else {
				if (this.getTechnique().toLowerCase().indexOf(filter) == -1 &&
						this.getLocation().toLowerCase().indexOf(filter) == -1 &&
						this.getYear().toLowerCase().indexOf(filter) == -1 &&
						this.getTitle().toLowerCase().indexOf(filter) == -1 && 
						this.getArtistLabel().toLowerCase().indexOf(filter) == -1) {
					return false;
				}
			}			
			
		}
		
		return true;
	}

	/** Get the Tool Tip Text for this Work of Art */
	getToolTip() {
		return this.getArtistLabel() + ' - ' + this.getTitle() + ' (' + this.getYear() + ')';
	}

	// print text of the size of the image
	getSize() {
		// sample values for width, length, height etc are: Rauminstallation, 50, 50cm
		// if we have numeric values, show them as "50 cm", otherwise use the text

		let buffer = '';

		if (hasNumeric(this.width) || hasNumeric(this.height) || hasNumeric(this.length)) {
			if (this.width != '') {
				if (buffer != '') {
					buffer += ' x ';
				}
				buffer += filterNonNumeric(this.width) + ' cm';
			}
			if (this.length != '') {
				if (buffer != '') {
					buffer += ' x ';
				}
				buffer += filterNonNumeric(this.length) + ' cm';
			}
			if (this.height != '') {
				if (buffer != '') {
					buffer += ' x ';
				}
				buffer += filterNonNumeric(this.height) + ' cm';
			}
		} else {
			if (this.width != '') {
				if (buffer != '') {
					buffer += ' x ';
				}
				buffer += this.width;
			}
			if (this.length != '') {
				if (buffer != '') {
					buffer += ' x ';
				}
				buffer += this.length;
			}
			if (this.height != '') {
				if (buffer != '') {
					buffer += ' x ';
				}
				buffer += this.height;
			}
		}
		return buffer;
	}

	/*	Pretty Printed Label for the Artists who created this work of art */
	getArtistLabel() {
		let buffer = '';
		for (let i = 0; i < this.artists.length; i++) {
			let name = this.artists [i].getFullName();
			if (name.trim() == '') {
				continue;
			}
			if (buffer != '') {
				buffer += ', ';
			}
			buffer += name;
		}
		if (buffer == '') {
			buffer = 'Unbekannt';
		}
		return buffer;
	}


	/*	Pretty Printed Label for Collections this Work of Art is in */
	getCollectionsLabel() {
		let buffer = '';
		for (let i = 0; i < this.collections.length; i++) {
			let name = this.collections [i].getName();
			if (name.trim() == '') {
				continue;
			}
			if (buffer != '') {
				buffer += ', ';
			}
			buffer += name;
		}
		return buffer;
	}
	
	getPrimaryImageAspectRatio() {
		if (this.image.getImageCount() > 0) {
			return this.image.getImageAt(0).getAspectRatio();
		} else {
			return 1;
		}
	}

	// Lookup the Selection State
	getIsInSelection() {
		return this.isInSelection;
	}

	// Update the Selection State
	setIsInSelection(value) {
		this.isInSelection = value;
	}

}